/**
 * useSecureAgency(agencyId) — small batching hook that requests bulk
 * trust-badge info from /api/agencies/security-badges/bulk.
 *
 * Many <PropertyCard> components on a search page each call this hook
 * with their own agency_id. We collect every requested id over a 60ms
 * window and fire a single bulk POST, then cache forever per session.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Module-level cache: agencyId → boolean (secure or not)
const cache = new Map();          // resolved values
const inflight = new Map();        // agencyId → Promise<boolean>
let pendingIds = new Set();        // ids waiting to flush
let flushTimer = null;             // pending setTimeout handle
const FLUSH_MS = 60;

const flush = async () => {
  flushTimer = null;
  const ids = Array.from(pendingIds);
  pendingIds = new Set();
  if (ids.length === 0) return;
  try {
    const res = await axios.post(`${BACKEND_URL}/api/agencies/security-badges/bulk`,
                                   { agency_ids: ids });
    const badges = res.data?.badges || {};
    ids.forEach((id) => {
      const v = !!badges[id];
      cache.set(id, v);
      const p = inflight.get(id);
      if (p && p.resolve) p.resolve(v);
      inflight.delete(id);
    });
  } catch (_) {
    // Failure: cache as false to avoid retry storms
    ids.forEach((id) => {
      cache.set(id, false);
      const p = inflight.get(id);
      if (p && p.resolve) p.resolve(false);
      inflight.delete(id);
    });
  }
};

const requestSecure = (agencyId) => {
  if (cache.has(agencyId)) return Promise.resolve(cache.get(agencyId));
  if (inflight.has(agencyId)) return inflight.get(agencyId).promise;
  let resolveFn;
  const promise = new Promise((res) => { resolveFn = res; });
  inflight.set(agencyId, { promise, resolve: resolveFn });
  pendingIds.add(agencyId);
  if (flushTimer == null) flushTimer = setTimeout(flush, FLUSH_MS);
  return promise;
};

export const useSecureAgency = (agencyId) => {
  const [secure, setSecure] = useState(
    agencyId && cache.has(agencyId) ? cache.get(agencyId) : false,
  );
  useEffect(() => {
    if (!agencyId) { setSecure(false); return; }
    if (cache.has(agencyId)) { setSecure(cache.get(agencyId)); return; }
    let cancelled = false;
    requestSecure(agencyId).then((v) => { if (!cancelled) setSecure(v); });
    return () => { cancelled = true; };
  }, [agencyId]);
  return secure;
};
